import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  User,
  Lock,
  CreditCard,
  Bell,
  KeyRound,
  Share2,
  Users,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

export interface NavItemConfig {
  key: string;
  label: string;
  icon: any;
  status: "active" | "link" | "soon";
  path?: string;
  badge?: string;
  subItems?: { key: string; label: string }[];
}

interface ProfileSidebarProps {
  currentSection: "profile" | "security";
  completionPct?: number;
}

export default function ProfileSidebar({ currentSection, completionPct = 50 }: ProfileSidebarProps) {
  const [integrationsOpen, setIntegrationsOpen] = useState(false);

  const NAV_CONFIG: NavItemConfig[] = [
    {
      key: "profile",
      label: "Profile",
      icon: User,
      status: "active",
      path: "/profile",
      badge: `${completionPct}%`,
    },
    {
      key: "security",
      label: "Security",
      icon: Lock,
      status: "active",
      path: "/profile/security",
    },
    {
      key: "subscription",
      label: "Subscription",
      icon: CreditCard,
      status: "link",
      path: "/subscription",
    },
    {
      key: "communication",
      label: "Communication",
      icon: Bell,
      status: "link",
      path: "/alerts",
    },
    {
      key: "api-access",
      label: "API Access",
      icon: KeyRound,
      status: "soon",
    },
    {
      key: "integrations",
      label: "Integrations",
      icon: Share2,
      status: "soon",
      subItems: [
        { key: "slack", label: "Slack" },
        { key: "teams", label: "Microsoft Teams" },
        { key: "webhooks", label: "Webhooks" },
        { key: "splunk", label: "Splunk" },
        { key: "stix", label: "STIX/TAXII" },
      ],
    },
    {
      key: "team",
      label: "Team",
      icon: Users,
      status: "soon",
    },
    {
      key: "invoices",
      label: "Invoices",
      icon: FileText,
      status: "soon",
    },
  ];

  return (
    <nav className="profile-nav-sidebar" aria-label="Account Settings Navigation">
      {NAV_CONFIG.map((item) => {
        const Icon = item.icon;

        // Active router items
        if (item.status === "active" && item.path) {
          const isActive =
            (item.key === "profile" && currentSection === "profile") ||
            (item.key === "security" && currentSection === "security");

          return (
            <NavLink
              key={item.key}
              to={item.path}
              className={`profile-nav-item${isActive ? " active" : ""}`}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Icon size={16} />
                <span>{item.label}</span>
              </div>

              {item.badge && completionPct < 100 && (
                <span
                  style={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    padding: "2px 6px",
                    borderRadius: 4,
                    background: isActive ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 102, 224, 0.15)",
                    color: isActive ? "#ffffff" : "var(--dash-indigo, #818cf8)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {item.badge}
                </span>
              )}
            </NavLink>
          );
        }

        // Link items to existing routes
        if (item.status === "link" && item.path) {
          return (
            <NavLink
              key={item.key}
              to={item.path}
              className="profile-nav-item"
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Icon size={16} />
                <span>{item.label}</span>
              </div>
            </NavLink>
          );
        }

        // Expandable "soon" group (Integrations)
        if (item.subItems) {
          return (
            <div key={item.key} style={{ display: "flex", flexDirection: "column" }}>
              <button
                type="button"
                onClick={() => setIntegrationsOpen(!integrationsOpen)}
                className="profile-nav-item"
                title="We're working on this"
                aria-label={`${item.label} - coming soon`}
                aria-disabled="true"
                style={{ width: "100%", justifyContent: "space-between" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Icon size={16} />
                  <span>{item.label}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="coming-soon-pill">COMING SOON</span>
                  {integrationsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
              </button>

              {integrationsOpen && (
                <div className="profile-nav-subgroup">
                  {item.subItems.map((sub) => (
                    <div
                      key={sub.key}
                      className="profile-nav-subitem"
                      title="We're working on this"
                      aria-label={`${sub.label} - coming soon`}
                    >
                      <span>{sub.label}</span>
                      <span className="coming-soon-pill" style={{ fontSize: "0.55rem" }}>COMING SOON</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }

        // Standard "soon" item
        return (
          <div
            key={item.key}
            className="profile-nav-item"
            aria-disabled="true"
            title="We're working on this"
            aria-label={`${item.label} - coming soon`}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Icon size={16} />
              <span>{item.label}</span>
            </div>
            <span className="coming-soon-pill">COMING SOON</span>
          </div>
        );
      })}
    </nav>
  );
}
